import { useEffect, useState } from 'react';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { Button } from '../../../../components/Button';
import { DateField } from '../../../../components/inputs/DateField';
import { FieldGroup } from '../../../../components/inputs/FieldGroup';
import { Textarea } from '../../../../components/inputs/Textarea';
import { TextField } from '../../../../components/inputs/TextField';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../lib/salutations';
import { focusValidationError } from '../../../../lib/validationFocus';
import { applicantsApi } from '../applicantsApi';
import type { ApplicantListItem } from '../applicantTypes';
import { sampleApplicant } from '../utils/sampleApplicant';

/**
 * Records or corrects one applicant.
 *
 * **The name is not on the record this form edits.** The row carries a `personId`; the name lives in
 * `rsys_pii`. On an EDIT the form asks for it through the applicant-scoped, AUDITED name route — so
 * the read fires on OPEN, never on mount: a modal nobody opened has audited nothing.
 *
 * **No status here.** An application starts Received and is moved by its own action, so a correction
 * to an email can never move a stage. **No date of birth** — nothing about applying needs it.
 */
export function ApplicantModal({
  postingId,
  editing,
  onClose,
  onSaved,
}: {
  postingId: string;
  /** Null adds one. */
  editing: ApplicantListItem | null;
  onClose: () => void;
  onSaved: (saved: ApplicantListItem) => void;
}) {
  const toast = useToast();

  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [email, setEmail] = useState(editing?.email ?? '');
  const [mobileNumber, setMobileNumber] = useState(editing?.mobileNumber ?? '');
  // A new application defaults to today: the form opens answerable rather than in error.
  const [dateApplied, setDateApplied] = useState(editing?.dateApplied ?? todayCalendarDate());
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');
  const [loading, setLoading] = useState(editing !== null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (editing === null) return;

    const controller = new AbortController();

    // The name comes from the OTHER system, and asking for it is the audited read.
    applicantsApi
      .name(editing.id, controller.signal)
      .then((name) => {
        if (controller.signal.aborted) return;
        setSalutation(name.salutation ?? '');
        setFirstName(name.firstName);
        setMiddleName(name.middleName ?? '');
        setLastName(name.lastName);
        setSuffix(name.suffix ?? '');
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        // No focus call: a failed load names no field.
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load this name.', 0));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [editing]);

  const valid = firstName.trim().length > 0 && lastName.trim().length > 0 && dateApplied.length > 0;
  const canSave = valid && confirmed && !saving && !loading;

  function generate() {
    const sample = sampleApplicant();
    setSalutation(sample.salutation);
    setFirstName(sample.firstName);
    setMiddleName(sample.middleName);
    setLastName(sample.lastName);
    setSuffix(sample.suffix);
    setEmail(sample.email);
    setMobileNumber(sample.mobileNumber);
    setDateApplied(sample.dateApplied);
    setRemarks(sample.remarks);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      salutation: salutation.trim() || null,
      firstName: firstName.trim(),
      middleName: middleName.trim() || null,
      lastName: lastName.trim(),
      suffix: suffix.trim() || null,
      email: email.trim() || null,
      mobileNumber: mobileNumber.trim() || null,
      dateApplied,
      remarks: remarks.trim() || null,
    };

    try {
      const saved = editing ? await applicantsApi.update(editing.id, request) : await applicantsApi.create(postingId, request);
      toast.success(editing ? 'Applicant updated.' : 'Applicant added.');
      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the applicant.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: 'applicant' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit applicant' : 'Add applicant'}
      size="2xl"
      id="applicant-modal"
      autogenerate={{ onGenerate: generate, disabled: saving || loading }}
      confirm={{ id: 'applicant-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="applicant-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="applicant-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* THE NAME ROW — three equal columns. These go to `rsys_pii` and nothing else here does. */}
        <div className="grid gap-4 sm:grid-cols-3">
          <FieldGroup
            label="First name"
            labelFor="applicant-first-name"
            description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
            segments={[
              {
                id: 'applicant-salutation',
                label: 'Salutation',
                value: salutation,
                onChange: setSalutation,
                options: SALUTATION_OPTIONS,
                allowCustom: true,
                error: error?.fieldError('salutation'),
                disabled: saving || loading,
                width: SALUTATION_SEGMENT_WIDTH,
                emptyLabel: SALUTATION_EMPTY_LABEL,
              },
              {
                id: 'applicant-first-name',
                label: 'First name',
                value: firstName,
                onChange: setFirstName,
                required: true,
                error: firstName.trim() ? error?.fieldError('firstName') : 'Enter a first name.',
                disabled: saving || loading,
              },
            ]}
          />

          <TextField
            id="applicant-middle-name"
            label="Middle name"
            description="Optional — plenty of people have none."
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            error={error?.fieldError('middleName')}
            disabled={saving || loading}
          />

          <FieldGroup
            label="Last name"
            description="The narrow box is the name extension — Jr., Sr., III."
            segments={[
              {
                id: 'applicant-last-name',
                label: 'Last name',
                value: lastName,
                onChange: setLastName,
                required: true,
                error: lastName.trim() ? error?.fieldError('lastName') : 'Enter a surname.',
                disabled: saving || loading,
              },
              {
                id: 'applicant-suffix',
                label: 'Suffix',
                value: suffix,
                onChange: setSuffix,
                error: error?.fieldError('suffix'),
                disabled: saving || loading,
                width: 'w-20 shrink-0',
              },
            ]}
          />
        </div>

        {/* The LOCAL half — none of this leaves rsys_. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="applicant-email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error?.fieldError('email')}
            disabled={saving || loading}
          />

          <TextField
            id="applicant-mobile-number"
            label="Mobile number"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            error={error?.fieldError('mobileNumber')}
            disabled={saving || loading}
          />

          <DateField
            id="applicant-date-applied"
            label="Date applied"
            description="The day the application was received."
            required
            max={todayCalendarDate()}
            value={dateApplied}
            onChange={setDateApplied}
            error={error?.fieldError('dateApplied')}
            disabled={saving || loading}
          />
        </div>

        {/* A free note about the application as a whole — full width, outside the grid. */}
        <Textarea
          id="applicant-remarks"
          label="Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving || loading}
        />
      </div>
    </Modal>
  );
}
